import { translate } from '@optimusgps/optimus-intl';
import { message } from 'antd';
import { AxiosResponseHeaders } from 'axios';
import copy from 'copy-to-clipboard';
import _ from 'lodash';
import { EventData, EventPayloadData, EventType, ParsedEvent } from '@/OptimusRoutes/types/rabbitMessage.types';
import { MULTIMEDIA_TYPE, ROLES } from '../constants/global';
import { CopyToClipBoard, EventMultimediaSource, ValidateMultimedia } from './utilities.type';

// export const minimunValidationForMultimedia = ({
// 	roles,
// 	shared = false,
// 	isImpersonating = false,
// }: ValidateMultimedia): boolean =>
// 	!isImpersonating && !shared && (roles.includes(ROLES.VIDEO_CAMERA) || roles.includes(ROLES.CUSTOMER_SUPPORT));

export const shouldDisplayMultimediaIcons = ({
	roles,
	shared = false,
	isImpersonating = false,
	impersonatorRoles = [],
}: ValidateMultimedia): boolean => {
	if (shared) {
		return false;
	}
	if (isImpersonating) {
		return (
			roles.includes(ROLES.VIDEO_CAMERA) &&
			(impersonatorRoles.includes(ROLES.CUSTOMER_SUPPORT) ||
				impersonatorRoles.includes(ROLES.SERVICE_PROVIDER) ||
				impersonatorRoles.includes(ROLES.MASTER))
		);
	}
	return roles.includes(ROLES.VIDEO_CAMERA);
};

export const shouldPlayMultimediaVideos = ({
	roles,
	shared = false,
	isImpersonating = false,
}: ValidateMultimedia): boolean => !shared && !isImpersonating && roles.includes(ROLES.VIDEO_CAMERA);

export const MULTIMEDIA_EVENT_TYPES: EventType[] = [
	MULTIMEDIA_TYPE.PHOTO as EventType,
	MULTIMEDIA_TYPE.VIDEO as EventType,
	MULTIMEDIA_TYPE.VIDEO_PHOTO as EventType,
];

export const isMultimediaEventType = (type?: EventType | null): type is EventType =>
	!!type && MULTIMEDIA_EVENT_TYPES.includes(type);

export const isMultimediaEventData = (data?: EventPayloadData | null): data is EventData =>
	isMultimediaEventType(_.get(data, 'type'));

export const getMultimediaEventData = ({ data }: EventMultimediaSource): EventData | null => {
	if (!isMultimediaEventData(data)) {
		return null;
	}

	return data;
};

export const getMultimediaEventType = (event: ParsedEvent | undefined): EventType | null => {
	if (!event || !isMultimediaEventData(event.data)) {
		return null;
	}

	return event.data.type;
};

export const collectEventMultimediaTypes = (event: EventMultimediaSource): EventType[] => {
	const payloadType = isMultimediaEventData(event.data) ? event.data.type : undefined;
	return _.uniq(_.filter([payloadType, ..._.map(event.multimedia, 'type')], isMultimediaEventType));
};

export const resolveMultimediaBadgeType = (types: EventType[]): EventType | null => {
	if (types.length === 0) {
		return null;
	}

	const hasVideo = types.includes(MULTIMEDIA_TYPE.VIDEO as EventType);
	const hasPhoto = types.includes(MULTIMEDIA_TYPE.PHOTO as EventType);

	if (hasPhoto && hasVideo) {
		return MULTIMEDIA_TYPE.VIDEO_PHOTO as EventType;
	}

	if (hasVideo) {
		return MULTIMEDIA_TYPE.VIDEO as EventType;
	}

	if (hasPhoto) {
		return MULTIMEDIA_TYPE.PHOTO as EventType;
	}

	return types[0];
};

export const getEventMultimediaBadgeType = (event: EventMultimediaSource): EventType | null =>
	resolveMultimediaBadgeType(collectEventMultimediaTypes(event));

export const hasEventVideoMultimedia = (event: EventMultimediaSource): boolean => {
	if (isMultimediaEventData(event.data) && event.data.type === MULTIMEDIA_TYPE.VIDEO) {
		return true;
	}

	return event.multimedia?.some((item) => item.type === MULTIMEDIA_TYPE.VIDEO) ?? false;
};

export const capitilizeFirstLetter = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export const copyToClipBoard = ({ text, intl, successMessage, errorMessage }: CopyToClipBoard) => {
	if (copy(text)) {
		if (successMessage) {
			message.success(successMessage);
		} else {
			message.success(intl.formatMessage(translate('successCopy')));
		}
	} else {
		if (successMessage) {
			message.error(errorMessage);
		} else {
			message.error(intl.formatMessage(translate('badCopy')));
		}
	}
};

export const getIntlEventMessageDescriptor = (eventId?: number) => {
	let eventKey = 'unknownEvent';
	if (eventId) {
		eventKey = `event${eventId}`;
	}
	return translate(eventKey);
};

export const downloadFile = (url: string, headers: AxiosResponseHeaders) => {
	const contentDisposition = headers['content-disposition'];
	const link = document.createElement('a');
	link.href = url;
	link.download = contentDisposition.split('filename=')[1].split(';')[0].replace(/['"]/g, '');
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	if (url.startsWith('blob:')) {
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 100);
	}
};
